import type { CSSProperties } from "react"

/**
 * Las ondas translúcidas del manual de identidad: dos o tres velos de borgoña que se
 * cruzan y crean un tono más profundo de la misma familia. **El único sitio que las
 * dibuja**, con los trazados del manual.
 *
 * ⚠ **`preserveAspectRatio="none"`**: la caja mide lo que mida la pieza —un lomo cambia
 * de ancho y una carátula pasa de carta a oficio— y la onda se estira con ella, siempre
 * vectorial.
 */

type Velo = { d: string; tono: "vino" | "hondo"; opacidad: number }

const PIEZAS: Record<string, { caja: string; velos: Velo[] }> = {
  caratula: {
    caja: "0 0 816 1056",
    velos: [
      { d: "M0 470 C110 560 190 780 180 1056 L0 1056 Z", tono: "vino", opacidad: 0.328 },
      { d: "M0 910 C260 970 560 980 816 890 L816 1056 L0 1056 Z", tono: "vino", opacidad: 0.53 },
      { d: "M110 1056 C380 1010 640 950 816 820 L816 1056 Z", tono: "vino", opacidad: 0.33 },
    ],
  },
  lomo: {
    caja: "0 0 225 64",
    velos: [
      { d: "M0 0 L225 0 L225 56 C150 40 70 30 0 22 Z", tono: "vino", opacidad: 0.33 },
      { d: "M0 0 L225 0 L225 18 C150 30 70 44 0 52 Z", tono: "hondo", opacidad: 0.33 },
      { d: "M0 0 L225 0 L225 10 C140 16 60 24 0 34 Z", tono: "vino", opacidad: 0.545 },
    ],
  },
  pestana: {
    caja: "0 0 20 58",
    velos: [
      { d: "M0 0 L16 0 C13 20 10 40 6 58 L0 58 Z", tono: "vino", opacidad: 0.335 },
      { d: "M0 0 L5 0 C8 20 10 40 13 58 L0 58 Z", tono: "vino", opacidad: 0.6 },
    ],
  },
  tapa: {
    caja: "0 0 80 484",
    velos: [
      { d: "M0 0 L72 0 C52 180 40 340 30 484 L0 484 Z", tono: "vino", opacidad: 0.33 },
      { d: "M0 0 L26 0 C38 170 52 330 62 484 L0 484 Z", tono: "hondo", opacidad: 0.328 },
      { d: "M0 0 L20 0 C30 200 28 360 18 484 L0 484 Z", tono: "vino", opacidad: 0.55 },
    ],
  },
  tejuelo: {
    caja: "0 0 559 110",
    velos: [
      { d: "M0 110 L0 30 C200 50 400 60 559 20 L559 110 Z", tono: "vino", opacidad: 0.328 },
      { d: "M0 110 L0 80 C180 50 380 40 559 60 L559 110 Z", tono: "hondo", opacidad: 0.324 },
      { d: "M200 110 C320 82 440 72 559 50 L559 110 Z", tono: "vino", opacidad: 0.542 },
    ],
  },
  muestra: {
    caja: "0 0 400 150",
    velos: [
      { d: "M0 150 L0 10 C90 30 150 100 170 150 Z", tono: "vino", opacidad: 0.332 },
      { d: "M0 150 L0 60 C150 90 280 100 400 50 L400 150 Z", tono: "hondo", opacidad: 0.325 },
      { d: "M110 150 C220 130 320 110 400 70 L400 150 Z", tono: "vino", opacidad: 0.542 },
    ],
  },
}

export function Ondas({ pieza, style }: { pieza: keyof typeof PIEZAS; style: CSSProperties }) {
  const dibujo = PIEZAS[pieza]
  if (!dibujo) return null
  return (
    <svg
      aria-hidden={true}
      viewBox={dibujo.caja}
      preserveAspectRatio="none"
      style={{ position: "absolute", display: "block", ...style }}
    >
      {dibujo.velos.map((velo) => (
        <path
          key={velo.d}
          d={velo.d}
          fillOpacity={velo.opacidad}
          style={{
            fill: velo.tono === "vino" ? "var(--impreso-vino)" : "var(--impreso-vino-hondo)",
          }}
        />
      ))}
    </svg>
  )
}
